import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';

import { GearIcon } from '@radix-ui/react-icons';
import { getRouteApi } from '@tanstack/react-router';

import EstateAnalysisPlaceholder from '@/assets/estate-analysis-placeholder.png';
import { closeModal, openModal } from '@/components/modals/modals.utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import Loader from '@/components/ui/loader';
import Spinner from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

import { useCreateEstateAnalysis, useDossierInfoValid, useEstateAnalysis } from '../api/analytics';
import { GeneralPriceValuation } from './general-price-valuation';
import { Isochrones } from './isochrones';
import type { EstateAnalysisSetting } from './modals/estate-analysis-setting-modal';
import { OfferSearch } from './offer-search';
import { OfferStatistics } from './offer-statistics';
import { PointsOfInterest } from './points-of-interest';
import { PricingHeatMap } from './pricing-heat-map';
import { PrintContainer } from './print-container';
import { ReportStartPage } from './report-start-page';
import { SocioEconomics } from './socio-economics';
import type { ExportPdfRef } from './types/export-pdf';

const route = getRouteApi(
  '/_authenticated/_sidebar-layout/estates/$estateId/_dashboard-layout/analytics'
);

export const AnalyticsDashboard = () => {
  const { t } = useTranslation();
  const estate = route.useLoaderData();
  const componentRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [exportSettings, updateExportSettings] = useState<EstateAnalysisSetting>({
    salesPrice: true,
    rentalPrice: true,
    confidence: false,
    salesPriceRange: true,
    rentalPriceRange: true,
    coordinates: false,
    currency: false,
    offerStatistics: true,
    pricingHeatMap: true,
    offerSearch: true,
    offers: true,
    socioEconomics: true,
    pointsOfInterest: true,
    isochrones: true,
  });

  const { data: dossierInfo, refetch: refetchDossierInfo } = useDossierInfoValid({ id: estate.id });

  const { data, isLoading, error, failureCount, refetch } = useEstateAnalysis({
    id: estate.id,
    queryOptions: {
      retry: true,
      retryDelay: 10_000,
    },
  });

  const { mutateAsync: createEstateAnalysis, isPending } = useCreateEstateAnalysis({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('requestAnalyticsSuccess'));
        refetchDossierInfo();
        refetch();
      },
      onError: (error) => {
        toast.error(error.message);
      },
    },
  });

  const handleRequestAnalytics = async () => {
    await createEstateAnalysis(estate.id);
  };

  const refs = {
    GeneralPriceValuation: useRef<ExportPdfRef>(null),
    SocioEconomics: useRef<ExportPdfRef>(null),
    Isochrones: useRef<ExportPdfRef>(null),
  };

  const handleBeforePrint = async () => {
    setIsPrinting(true);
    const exportPdfProcesses = Object.values(refs).map((el) => el.current?.startExportPdf?.());
    await Promise.all(exportPdfProcesses);
  };

  const handleAfterPrint = () => {
    Object.values(refs).map((el) => el.current?.finishExportPdf?.());
    setIsPrinting(false);
  };

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: 'PriceHubble-report',
    onBeforePrint: handleBeforePrint,
    onAfterPrint: handleAfterPrint,
  });

  const handleOpenSetting = () => {
    openModal('estate-analysis-setting', {
      defaultCheckedFields: exportSettings,
      onSubmit: (data) => {
        updateExportSettings(data);
        closeModal();
      },
    });
  };

  const isAllExportSettingsFalse = useCallback(
    (keys: Array<keyof EstateAnalysisSetting>) => keys.every((key) => !exportSettings[key]),
    [exportSettings]
  );

  const { isGeneralPriceValuationHidden, isPropertyPricesLastReportPage } = useMemo(
    () => ({
      isGeneralPriceValuationHidden: isAllExportSettingsFalse([
        'salesPrice',
        'rentalPrice',
        'confidence',
        'salesPriceRange',
        'rentalPriceRange',
        'coordinates',
        'currency',
      ]),
      isPropertyPricesLastReportPage: isAllExportSettingsFalse([
        'isochrones',
        'pointsOfInterest',
        'socioEconomics',
        'offerSearch',
      ]),
    }),
    [isAllExportSettingsFalse]
  );

  if (isLoading || isPending || !data || error) {
    return (
      <div className="relative flex justify-center">
        <div className="relative inline-block">
          <img src={EstateAnalysisPlaceholder} alt="Estate Analysis" className="w-full blur-md" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2 text-center">
              <h3 className="text-md font-bold md:text-2xl">
                {failureCount < 3 ? t('gatheringEstateAnalysisData') : t('analyticsRetrievalError')}
              </h3>
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="size-full space-y-6 pb-6">
      {dossierInfo?.isDossierInfoValid ? null : (
        <Card className="space-y-4 rounded-md p-6">
          <div className="rounded border border-caution bg-caution/5 p-6 text-base font-normal text-caution">
            {t('analyticsOutOfDate')}
          </div>
          <div className="flex justify-end gap-4">
            <Button
              variant="contained"
              onClick={handleRequestAnalytics}
              size="md"
              className="!px-12"
            >
              {t('requestAnalytics')}
            </Button>
          </div>
        </Card>
      )}
      <div
        ref={componentRef}
        className="space-y-6 bg-background-lighter [&>div:nth-child(2)]:!mt-0"
      >
        <PrintContainer estate={estate} header={t('propertyValuation')}>
          <ReportStartPage estate={estate} estateAnalysis={data.analysis} />
        </PrintContainer>
        <PrintContainer
          estate={estate}
          header={t('propertyPrices')}
          hide={isGeneralPriceValuationHidden && !exportSettings.offerStatistics}
          isLastPage={isPropertyPricesLastReportPage}
        >
          <div className="mb-6 flex items-center justify-between print:hidden">
            <h2 className="text-md font-bold text-primary-dark">{t('generalPriceValuation')}</h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handlePrint} disabled={isPrinting}>
                {`${t('export')} .pdf`}
              </Button>
              <Button
                variant="outline"
                onClick={handleOpenSetting}
                className="!px-3"
                disabled={isPrinting}
              >
                <GearIcon />
              </Button>
            </div>
          </div>
          <GeneralPriceValuation
            ref={refs.GeneralPriceValuation}
            data={estate}
            estateAnalysis={data.analysis}
            exportSettings={exportSettings}
            hideOnPrint={isGeneralPriceValuationHidden}
          />
          <h3
            className={cn(
              'my-4 hidden text-[14px]',
              exportSettings.offerStatistics && 'print:block'
            )}
          >
            {t('salesPrice')}
          </h3>
          <OfferStatistics
            data={estate}
            defaultDealType="sale"
            estateAnalysis={data.analysis}
            hideOnPrint={!exportSettings.offerStatistics}
          />
          {isPrinting && exportSettings.offerStatistics ? (
            <div className={cn('print:mt-2 print:space-y-2')}>
              <h3 className="my-4 text-[14px]">{t('rentalPrice')}</h3>
              <OfferStatistics
                data={estate}
                defaultDealType="rent_gross"
                estateAnalysis={data.analysis}
              />
            </div>
          ) : null}
        </PrintContainer>
        {isPrinting ? null : (
          <PrintContainer estate={estate} hide>
            <PricingHeatMap data={estate} estateAnalysis={data.analysis} />
          </PrintContainer>
        )}
        <PrintContainer
          estate={estate}
          header={t('offerSearch')}
          hide={!exportSettings.offerSearch}
          isLastPage={
            !exportSettings.isochrones &&
            !exportSettings.pointsOfInterest &&
            !exportSettings.socioEconomics
          }
        >
          <OfferSearch data={estate} estateAnalysis={data.analysis} />
        </PrintContainer>
        <SocioEconomics
          ref={refs.SocioEconomics}
          data={estate}
          estateAnalysis={data.analysis}
          hideOnPrint={!exportSettings.socioEconomics}
          isLastPrintPage={!exportSettings.isochrones && !exportSettings.pointsOfInterest}
        />
        <PrintContainer
          estate={estate}
          header={t('pointsOfInterest')}
          hide={!exportSettings.pointsOfInterest}
          isLastPage={!exportSettings.isochrones}
        >
          <PointsOfInterest data={estate} estateAnalysis={data.analysis} />
        </PrintContainer>
        <PrintContainer
          estate={estate}
          header={t('isochrones')}
          isLastPage={exportSettings.isochrones}
          hide={!exportSettings.isochrones}
        >
          <Isochrones ref={refs.Isochrones} data={estate} estateAnalysis={data.analysis} />
        </PrintContainer>
      </div>
      {isPrinting ? (
        <div className="absolute left-0 top-0 z-[9999] !m-0 flex h-screen w-screen items-center justify-center bg-black opacity-50">
          <Spinner className="size-12 [&_*]:stroke-primary-dark" />
        </div>
      ) : null}
    </div>
  );
};
